import styled from 'styled-components'
import ReactGA from 'react-ga4'
import {useNavigate} from 'react-router-dom'
import {Button, IconButton, InputAdornment, TextField, Tooltip} from '@mui/material'
import AddOutlinedIcon from '@mui/icons-material/AddOutlined'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import MoreHorizOutlinedIcon from '@mui/icons-material/MoreHorizOutlined'
import LeftBar from '../home/components/leftBar'
import {getCurrentColors} from '../../utils/colors'
import {countFiles, KBS, PINECONE_INDEXES} from './mockData'
import {SyncPill} from './shared'

const COLORS = getCurrentColors()

const Page = styled.div`
    display: flex;
    height: 100vh;
    overflow: hidden !important;
`

const Main = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
    background: #f7f9fa;
`

const Scroll = styled.div`
    flex: 1;
    overflow-y: auto;
`

const Wrap = styled.div`
    max-width: 1080px;
    margin: 0 auto;
    padding: 40px 26px 60px;
`

const PageHead = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 26px;

    h1 {
        font-size: 25px;
        color: #0c1f29;
        letter-spacing: -0.03em;
        margin: 0 0 5px;
    }

    p {
        color: #5d6b72;
        font-size: 14px;
        margin: 0;
    }
`

const SearchBox = styled.div`
    max-width: 320px;
    margin-bottom: 18px;
`

const Grid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 14px;
`

const IndexCard = styled.div`
    text-align: left;
    background: #ffffff;
    border: 1px solid #e6eaec;
    border-radius: 12px;
    padding: 18px;
    cursor: pointer;
    transition: border-color 0.15s, box-shadow 0.15s;

    &:hover {
        border-color: ${COLORS.primariaEscura};
        box-shadow: 0 6px 20px rgba(12, 31, 41, 0.07);
    }
`

const IndexTop = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 14px;
`

const IndexIcon = styled.div`
    width: 40px;
    height: 40px;
    border-radius: 11px;
    background: ${COLORS.primaria};
    display: grid;
    place-items: center;
    flex-shrink: 0;

    svg {
        color: ${COLORS.primariaEscura};
    }
`

const IndexName = styled.div`
    font-weight: 600;
    font-size: 15px;
    color: #0c1f29;
    line-height: 1.3;
`

const IndexDrive = styled.div`
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 11.5px;
    color: #8a969c;
    margin-top: 3px;

    svg {
        font-size: 13px;
    }
`

const IndexStats = styled.div`
    display: flex;
    gap: 18px;
    padding-top: 14px;
    border-top: 1px solid #e6eaec;
    margin-top: 8px;
`

const IndexStatValue = styled.div`
    font-weight: 600;
    font-size: 16px;
    color: #0c1f29;
    font-variant-numeric: tabular-nums;
`

const IndexStatLabel = styled.div`
    font-size: 11px;
    color: #8a969c;
    margin-top: 1px;
`

export default function BasesDeDados() {
    const navigate = useNavigate()

    ReactGA.send({
        hitType: 'pageview',
        page: '/bases-de-dados',
        title: 'Bases de dados',
    })

    return (
        <Page>
            <LeftBar />
            <Main>
                <Scroll>
                    <Wrap>
                        <PageHead>
                            <div>
                                <h1>Bases de dados</h1>
                                <p>
                                    KBs vinculadas a pastas do Google Drive.
                                    Dentro de cada KB você cria os índices do
                                    Pinecone e associa os documentos a eles.
                                </p>
                            </div>
                            <Button
                                variant="contained"
                                startIcon={<AddOutlinedIcon />}
                                onClick={() => navigate('/bases-de-dados/novo')}
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: '9px',
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                Novo KB
                            </Button>
                        </PageHead>

                        <SearchBox>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Buscar índice…"
                                disabled
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchOutlinedIcon
                                                sx={{
                                                    fontSize: 18,
                                                    color: '#8a969c',
                                                }}
                                            />
                                        </InputAdornment>
                                    ),
                                    sx: {borderRadius: '9px', background: '#fff'},
                                }}
                            />
                        </SearchBox>

                        <Grid>
                            {KBS.map((kb) => {
                                const lastIndex =
                                    PINECONE_INDEXES[kb.name]?.[0]
                                return (
                                    <IndexCard
                                        key={kb.name}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() =>
                                            navigate(
                                                `/bases-de-dados/${kb.name}`
                                            )
                                        }
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter')
                                                navigate(
                                                    `/bases-de-dados/${kb.name}`
                                                )
                                        }}
                                    >
                                        <IndexTop>
                                            <IndexIcon>
                                                <StorageOutlinedIcon />
                                            </IndexIcon>
                                            <div
                                                style={{
                                                    flex: 1,
                                                    minWidth: 0,
                                                }}
                                            >
                                                <IndexName>
                                                    {kb.name}
                                                </IndexName>
                                                <IndexDrive>
                                                    <FolderOutlinedIcon />
                                                    {kb.drivePath}
                                                </IndexDrive>
                                            </div>
                                            <IconButton
                                                size="small"
                                                aria-label="Opções"
                                                disabled
                                            >
                                                <MoreHorizOutlinedIcon
                                                    sx={{fontSize: 18}}
                                                />
                                            </IconButton>
                                        </IndexTop>
                                        <SyncPill status={kb.lastSync}>
                                            {kb.lastSyncLabel}
                                        </SyncPill>
                                        <IndexStats>
                                            <div>
                                                <IndexStatValue>
                                                    {countFiles(kb.root)}
                                                </IndexStatValue>
                                                <IndexStatLabel>
                                                    Documentos
                                                </IndexStatLabel>
                                            </div>
                                            <div>
                                                <IndexStatValue>
                                                    {lastIndex?.name ?? '—'}
                                                </IndexStatValue>
                                                <IndexStatLabel>
                                                    Último índice
                                                </IndexStatLabel>
                                            </div>
                                        </IndexStats>
                                    </IndexCard>
                                )
                            })}
                        </Grid>
                    </Wrap>
                </Scroll>
            </Main>
        </Page>
    )
}
